import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// Builds the same three markup shapes the old string templates produced, as DOM nodes rather than
// an innerHTML-assigned string, so a value arriving off the wire (the ActionCable `url`) can never
// be interpreted as markup. `this.element.replaceChildren(...)` replaces the holder's contents the
// same way the innerHTML assignments it replaces did.
const buildExportingProgress = (progress) => {
  const wrapper = document.createElement("div")
  wrapper.className = "progress exporter-progress"

  const bar = document.createElement("div")
  bar.setAttribute("aria-valuemax", "100")
  bar.setAttribute("aria-valuemin", "0")
  bar.setAttribute("aria-valuenow", String(progress))
  bar.className = "progress-bar progress-bar-info progress-bar-striped active"
  bar.setAttribute("role", "progressbar")
  bar.style.width = `${progress}%`

  const idle = document.createElement("div")
  idle.className = "progress-bar progress-bar-idle progress-bar-striped active"
  idle.style.width = "100%"

  const label = document.createElement("span")
  label.textContent = "Generating Export..."

  wrapper.append(bar, idle, label)
  return wrapper
}

const buildExportLink = (url) => {
  const link = document.createElement("a")
  link.className = "btn btn-primary hidden-xs"
  link.id = "export-button"
  link.setAttribute("href", url)
  link.textContent = "Export Data"
  return link
}

const buildDownloadLink = (url) => {
  const link = document.createElement("a")
  link.target = "_blank"
  link.id = "download-button"
  link.className = "btn btn-primary hidden-xs"
  link.setAttribute("href", url)
  link.textContent = "Download Exported Data"
  return link
}

export default class extends Controller {
  initialize() {
    this.onClick = this.onClick.bind(this)
  }

  connect() {
    this.element.addEventListener("click", this.onClick)

    const exportEvent = this.currentExport()
    if (exportEvent && (exportEvent.stage === "waiting" || exportEvent.stage === "in_progress")) {
      this.subscribeToExport(exportEvent.id)
    }
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick)
  }

  // Delegated from the mount: the handlers replace the very markup carrying #export-button /
  // #download-button, so a data-action would not survive the swap.
  onClick(event) {
    if (event.target.closest("#export-button")) {
      this.start(event)
    } else if (event.target.closest("#download-button")) {
      this.resetToExportLink()
    }
  }

  start(event) {
    event.preventDefault()

    const params = this.currentDatatableParams()
    const url = `${event.target.closest("#export-button").getAttribute("href")}?${$.param(params)}`

    this.element.replaceChildren(buildExportingProgress(0))

    const options = RequestHelper.defaultRequestOptions()
    options.headers = Object.assign({}, options.headers, {
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*"
    })

    fetch(url, options).
      then(response => {
        if (!response.ok) { throw new Error(`Export request failed (${response.status})`) }
        return response.json()
      }).
      then(body => {
        // A plain object body has no `.length`, so this guard is always truthy and always
        // subscribes.
        if (body.length !== 0) { this.subscribeToExport(body.export_id) }
      }).
      catch(this.logFailedCall)
  }

  resetToExportLink() {
    this.element.replaceChildren(buildExportLink(this.element.getAttribute("data-export-url")))
  }

  // `data-export` carries the ExportEvent as JSON, the same shape jQuery's `.data()` used to
  // parse for us; `data-export-url` is a plain string, read as-is. Both stay their existing
  // attribute names -- this partial's `stimulus_controller` local lets it mount a different
  // controller elsewhere, so hard-coding a Values-API-shaped attribute name here would break that.
  currentExport() {
    const raw = this.element.getAttribute("data-export")
    return raw ? JSON.parse(raw) : null
  }

  subscribeToExport(exportId) {
    const subscription = App.cable.subscriptions.create(
      { channel: "GiftCustomerExportsChannel", id: exportId },
      { received: (data) => this.receive(subscription, data) }
    )
  }

  receive(subscription, data) {
    const bar = this.element.querySelector(".progress-bar[aria-valuenow]")
    // In case subscription messages come in a weird order, we keep the highest progress
    const currentProgress = parseInt(bar ? bar.getAttribute("aria-valuenow") : undefined)
    const newProgress = currentProgress > data.event.progress ? currentProgress : data.event.progress

    if (data.event.stage === "in_progress" || data.event.stage === "waiting") {
      const info = this.element.querySelector(".progress-bar-info")
      if (info) { info.style.width = `${newProgress}%` }
    } else if (data.event.stage === "complete") {
      subscription.unsubscribe()
      this.element.replaceChildren(buildDownloadLink(data.event.url))
    }
  }

  // Reads the first `.datatable` on the page, unscoped - the table belongs to the page, not to
  // this controller's mount. Same `data-source` convention as gift_events/thank_yous/gift_cancels
  // etc. (decisions.md D8) -- it's read off an element this controller doesn't own, so it isn't a
  // Values API candidate.
  currentDatatableParams() {
    const table = document.querySelector(".datatable")
    const source = table ? table.getAttribute("data-source") : ""
    return this.urlParamsAsObject(source || "")
  }

  urlParamsAsObject(url) {
    const query = url.substring(url.indexOf("?") + 1)
    const regex = /([^&=]+)=?([^&]*)/g
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "))

    const params = {}
    let match
    while ((match = regex.exec(query))) {
      const key = decode(match[1])
      const value = decode(match[2])
      if (key.substring(key.length - 2) === "[]") {
        const arrayKey = key.substring(0, key.length - 2)
        params[arrayKey] = params[arrayKey] || []
        params[arrayKey].push(value)
      } else {
        params[key] = value
      }
    }

    const assign = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1
      let target = obj
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i]
        if (!(key in target)) { target[key] = {} }
        target = target[key]
      }
      target[keyPath[lastKeyIndex]] = value
    }

    Object.keys(params).forEach(prop => {
      const structure = prop.split("[")
      if (structure.length > 1) {
        const levels = structure.map(part => part.replace(/[?[\]\\ ]/g, ""))
        assign(params, levels, params[prop])
        delete params[prop]
      }
    })

    return params
  }

  logFailedCall(response) {
    console.warn(response)
  }
}
